import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER_UPERCUT } from '@gql/jobs';
import { useDebounce } from '@hooks/useDebounce';
import { usePageSize } from '@hooks/usePageSize';
import { useGetJobApplications } from '@modules/jobApplications/hooks/useGetJobApplications';
import { useGetJobApplicationsKanban } from '@modules/jobApplications/hooks/useGetJobApplicationsKanban';
import {
    defaultJobApplicationFilters,
    IJobApplicationDetailFilter,
} from '@modules/jobApplications/JobApplicationFilter';
import JobApplicationListContainer from '@modules/jobApplications/JobApplicationListContainer';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

const Container = styled(Box)(({ theme }) => ({
    padding: '0px 0px 16px',
    minHeight: 'calc(100dvh - 118px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    background: theme.palette.neutralBlack.main,
}));

const JobOfferApplication = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'job-offer-applications-page-size' });

    const statusParam = searchParams.get('status');
    const selectedStatuses = useMemo(() => statusParam?.split(',').filter(Boolean) ?? [], [statusParam]);

    const [searchQuery, setSearchQuery] = useState({
        query: '',
        pagination: {
            page: 1,
            limit: pageSize,
        },
        sortBy: { field: 'createdDate', order: -1 },
        filter: {
            ...defaultJobApplicationFilters,
            jobNumber: slug ? [slug] : undefined,
            jobApplicationStatus: selectedStatuses,
        },
    });

    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    useEffect(() => {
        setSearchQuery(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                jobNumber: slug ? [slug] : undefined,
            },
        }));
    }, [slug]);

    useEffect(() => {
        setSearchQuery(prev => {
            const currentStatuses = prev.filter.jobApplicationStatus ?? [];
            const hasSameStatuses =
                currentStatuses.length === selectedStatuses.length &&
                currentStatuses.every((status, index) => status === selectedStatuses[index]);

            if (hasSameStatuses) {
                return prev;
            }

            return {
                ...prev,
                pagination: { ...prev.pagination, page: 1 },
                filter: {
                    ...prev.filter,
                    jobApplicationStatus: selectedStatuses,
                },
            };
        });
    }, [selectedStatuses]);

    // Validate job offer exists
    const {
        data: jobOfferData,
        loading: jobOfferLoading,
        error: jobOfferError,
    } = useQuery(GET_JOB_OFFER_UPERCUT, {
        variables: { jobNumber: slug, showInactive: true },
        skip: !slug,
    });

    useEffect(() => {
        if (!slug) {
            navigate('/job-offers');
            return;
        }

        if (!jobOfferLoading && !jobOfferData?.getJobOfferUpercut && !jobOfferError) {
            navigate('/job-offers');
        }
    }, [slug, jobOfferData, jobOfferLoading, jobOfferError, navigate]);

    const onQueryChange = useCallback((value: string) => {
        setSearchQuery(prev => ({
            ...prev,
            pagination: { ...prev.pagination, page: 1 },
            query: value,
        }));
    }, []);

    const onSortChange = useCallback((newSortBy: { field: string; order: number }) => {
        setSearchQuery(prev => ({
            ...prev,
            sortBy: newSortBy,
        }));
    }, []);

    const onFilterChange = useCallback(
        (newFilter: IJobApplicationDetailFilter) => {
            const nextStatuses = newFilter.jobApplicationStatus ?? [];

            setSearchQuery(prev => ({
                ...prev,
                pagination: { ...prev.pagination, page: 1 },
                filter: {
                    ...newFilter,
                    jobNumber: slug ? [slug] : undefined,
                    jobApplicationStatus: nextStatuses,
                },
            }));

            setSearchParams(
                prevParams => {
                    const nextParams = new URLSearchParams(prevParams);

                    if (nextStatuses.length > 0) {
                        nextParams.set('status', nextStatuses.join(','));
                    } else {
                        nextParams.delete('status');
                    }

                    return nextParams;
                },
                { replace: true },
            );
        },
        [setSearchParams, slug],
    );

    // Fetch data for list/grid view (paginated)
    const {
        jobApplications,
        loading: listLoading,
        isLoadingMore,
        error: listError,
        totalDocs: listTotalDocs,
        totalPages,
        loadMore: loadMoreList,
        forceRefresh,
    } = useGetJobApplications(
        debouncedSearchQuery.query,
        debouncedSearchQuery.filter,
        debouncedSearchQuery.sortBy,
        debouncedSearchQuery.pagination.page - 1,
        debouncedSearchQuery.pagination.limit,
    );

    // Fetch data for kanban view (infinite scroll per status)
    const { kanbanData, loadMore } = useGetJobApplicationsKanban(
        debouncedSearchQuery.query,
        debouncedSearchQuery.filter,
        debouncedSearchQuery.sortBy,
    );

    const handleStatusUpdate = useCallback((applicationId: number, newStatus: JobApplicationStatus) => {
        // TODO: Implement GraphQL mutation to update job application status
        console.log('Update job application status:', { applicationId, newStatus });
        // After successful update, refetch the data
    }, []);

    if (jobOfferError) {
        console.error('Error fetching job offer:', jobOfferError);
        return (
            <Container>
                <div>Job Offer is either inactive or not found</div>
            </Container>
        );
    }

    if (listError) {
        console.error('Error fetching job applications:', listError);
        return (
            <Container>
                <div>
                    Error fetching job applications <pre>{JSON.stringify(listError, null, 2)}</pre>
                </div>
            </Container>
        );
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <JobApplicationListContainer
                data={jobApplications}
                kanbanData={kanbanData}
                loading={listLoading || jobOfferLoading}
                isLoadingMore={isLoadingMore}
                totalDocs={listTotalDocs}
                totalPages={totalPages}
                meta={{
                    totalDocs: listTotalDocs,
                    page: searchQuery.pagination.page,
                    limit: searchQuery.pagination.limit,
                }}
                currentPage={searchQuery.pagination.page}
                itemsPerPage={searchQuery.pagination.limit}
                onPageChange={page => {
                    setSearchQuery(prev => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                    }));
                }}
                onItemsPerPageChange={limit => {
                    storePageSize(limit);
                    setSearchQuery(prev => ({
                        ...prev,
                        pagination: { ...prev.pagination, page: 1, limit },
                    }));
                }}
                onQueryChange={onQueryChange}
                onSortChange={onSortChange}
                onFilterChange={onFilterChange}
                filter={searchQuery.filter}
                displayMode="talent-focus"
                onLoadMoreKanban={loadMore}
                onLoadMore={loadMoreList}
                onStatusUpdate={handleStatusUpdate}
                hideChipKeys={['jobNumber']}
                onRefresh={forceRefresh}
            />
        </Box>
    );
};

export default JobOfferApplication;
